/**
 * Metadata Interpreter - Core Engine
 *
 * Parses workflow template metadata and:
 * 1. Validates against DSL schema v2.0
 * 2. Generates UI using React JSON Schema Form
 * 3. Maps form data to FERP primitives
 * 4. Applies transformations
 * 5. Maintains complete audit trail
 *
 * Single code path: Metadata → UI → Primitives
 * Life-critical: Every operation logged
 * Domain-agnostic: Works for any domain via metadata
 */

import React, { useState, useEffect } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { RJSFSchema, UiSchema, FieldTemplateProps } from '@rjsf/utils';
import { Stack, Button, Group, Stepper, Text, Paper, Alert, Badge, Collapse, List, Code } from '@mantine/core';
import { IconCheck, IconAlertCircle, IconAlertTriangle, IconInfoCircle, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import {
  WorkflowTemplateMetadata,
  PrimitiveFieldMapping,
  TransformationSpec,
} from './types/MetadataTypes';
import {
  SIXTEEN_PRIMITIVE_TYPES,
  assertValidPrimitiveType,
  VALIDATION_ERROR_CODES,
  isValidPrimitiveType,
} from './constants/DSLConstants';
import { TransformationEngine } from './TransformationEngine';
import { AuditTrailCollector, AuditEntry } from './AuditTrailCollector';
import { MANTINE_WIDGETS } from './MantineWidgets';
import { ValidationResult, ValidationViolation } from './services/MetadataTemplateService';
import * as jsonpath from 'jsonpath-plus';

/**
 * Custom Field Template - Suppresses RJSF's default label
 * Mantine widgets render their own labels, so we don't need RJSF's wrapper
 */
function CustomFieldTemplate(props: FieldTemplateProps) {
  const { children, errors, help, hidden } = props;

  if (hidden) {
    return <div style={{ display: 'none' }}>{children}</div>;
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      {children}
      {errors}
      {help}
    </div>
  );
}

/**
 * Violations Display Component
 *
 * Displays validation violations from Phase 11 Validation Framework
 * Shows severity badges, fix suggestions, and standards references
 */
interface ViolationsDisplayProps {
  violations: ValidationViolation[];
  validationResult: ValidationResult;
}

function ViolationsDisplay({ violations, validationResult }: ViolationsDisplayProps) {
  const [expandedViolations, setExpandedViolations] = useState<Set<number>>(new Set());

  const toggleViolation = (index: number) => {
    const newExpanded = new Set(expandedViolations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedViolations(newExpanded);
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'ERROR':
        return 'red';
      case 'WARNING':
        return 'yellow';
      case 'INFO':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'ERROR':
        return <IconAlertCircle size={16} />;
      case 'WARNING':
        return <IconAlertTriangle size={16} />;
      case 'INFO':
        return <IconInfoCircle size={16} />;
      default:
        return <IconInfoCircle size={16} />;
    }
  };

  if (violations.length === 0) {
    return (
      <Alert color="green" title="Validation Passed" icon={<IconCheck />}>
        Template metadata passed all {validationResult.totalLayers} validation layers successfully.
      </Alert>
    );
  }

  const errorCount = violations.filter((v) => v.severity === 'ERROR').length;
  const warningCount = violations.filter((v) => v.severity === 'WARNING').length;
  const infoCount = violations.filter((v) => v.severity === 'INFO').length;

  return (
    <Stack gap="md">
      <Alert
        color={errorCount > 0 ? 'red' : warningCount > 0 ? 'yellow' : 'blue'}
        title={`Validation Issues Found (${violations.length})`}
        icon={errorCount > 0 ? <IconAlertCircle /> : <IconAlertTriangle />}
      >
        <Group gap="md">
          {errorCount > 0 && (
            <Badge color="red" leftSection={getSeverityIcon('ERROR')}>
              {errorCount} Errors
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge color="yellow" leftSection={getSeverityIcon('WARNING')}>
              {warningCount} Warnings
            </Badge>
          )}
          {infoCount > 0 && (
            <Badge color="blue" leftSection={getSeverityIcon('INFO')}>
              {infoCount} Info
            </Badge>
          )}
        </Group>
        <Text size="sm" mt="xs">
          Validated {validationResult.layersExecuted} of {validationResult.totalLayers} layers
        </Text>
      </Alert>

      <Stack gap="xs">
        {violations.map((violation, index) => {
          const isExpanded = expandedViolations.has(index);
          const hasSuggestion = violation.fixSuggestion && Object.keys(violation.fixSuggestion).length > 0;

          return (
            <Paper key={index} p="md" withBorder>
              <Group justify="space-between" onClick={() => toggleViolation(index)} style={{ cursor: 'pointer' }}>
                <Group gap="sm">
                  <Badge color={getSeverityColor(violation.severity)} leftSection={getSeverityIcon(violation.severity)}>
                    {violation.severity}
                  </Badge>
                  <Text fw={500}>{violation.message}</Text>
                </Group>
                {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              </Group>

              <Collapse in={isExpanded}>
                <Stack gap="sm" mt="md">
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">
                      Layer: <Code>{violation.layer}</Code>
                    </Text>
                    {violation.field && (
                      <Text size="sm" c="dimmed">
                        Field: <Code>{violation.field}</Code>
                      </Text>
                    )}
                    <Text size="sm" c="dimmed">
                      Code: <Code>{violation.code}</Code>
                    </Text>
                    <Text size="sm" c="dimmed">
                      Reference: <Code>{violation.reference}</Code>
                    </Text>
                  </Group>

                  {hasSuggestion && (
                    <div>
                      <Text size="sm" fw={600} mb="xs">
                        Fix Suggestion:
                      </Text>
                      {violation.fixSuggestion.add_field && (
                        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                          <Text size="sm" fw={500}>
                            Add Field
                          </Text>
                          <List size="sm" mt="xs">
                            <List.Item>
                              Pattern: <Code>{violation.fixSuggestion.add_field.pattern}</Code>
                            </List.Item>
                            <List.Item>
                              Display Name: {violation.fixSuggestion.add_field.display_name}
                            </List.Item>
                            <List.Item>
                              Data Type: {violation.fixSuggestion.add_field.data_type}
                            </List.Item>
                            {violation.fixSuggestion.add_field.edi_segment && (
                              <List.Item>
                                EDI Segment: {violation.fixSuggestion.add_field.edi_segment}
                              </List.Item>
                            )}
                          </List>
                        </Alert>
                      )}
                      {violation.fixSuggestion.split_into && (
                        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                          <Text size="sm" fw={500}>
                            Split Field Into:
                          </Text>
                          <List size="sm" mt="xs">
                            {violation.fixSuggestion.split_into.map((field: any, idx: number) => (
                              <List.Item key={idx}>
                                {field.label} → <Code>{field.maps_to}</Code>
                              </List.Item>
                            ))}
                          </List>
                        </Alert>
                      )}
                    </div>
                  )}
                </Stack>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}

/**
 * Stepper Form Wrapper Component
 * Manages multi-step form with validation and navigation
 */
interface StepperFormWrapperProps {
  interpreter: MetadataInterpreter;
  onSubmit?: (primitives: { nodes: PrimitiveNode[]; connections: any[] }) => void;
  validationResult?: ValidationResult;
}

function StepperFormWrapper({ interpreter, onSubmit, validationResult }: StepperFormWrapperProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [stepErrors, setStepErrors] = useState<Record<number, boolean>>({});
  const [highestStepReached, setHighestStepReached] = useState(0);

  const metadata = interpreter.getMetadata();
  const steps = metadata.ui.steps;
  const totalSteps = steps.length;
  const isLastStep = activeStep === totalSteps - 1;

  // Check for ERROR-level violations
  const hasErrorViolations = validationResult && validationResult.violations.some((v) => v.severity === 'ERROR');
  const canSubmit = !hasErrorViolations;

  // Validate current step
  const validateCurrentStep = (): boolean => {
    const currentStep = steps[activeStep];
    if (!currentStep) return false;
    const requiredFields = currentStep.fields.filter((f) => f.required).map((f) => f.id);
    const formData = interpreter.getFormData();

    // Check if all required fields have values
    const allRequiredFilled = requiredFields.every(
      (fieldId) =>
        formData[fieldId] !== undefined && formData[fieldId] !== '' && formData[fieldId] !== null,
    );

    setStepErrors((prev) => ({ ...prev, [activeStep]: !allRequiredFilled }));
    return allRequiredFilled;
  };

  // Handle next button
  const handleNext = () => {
    if (validateCurrentStep()) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      if (nextStep > highestStepReached) {
        setHighestStepReached(nextStep);
      }
    }
  };

  // Handle previous button
  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Handle final submission
  const handleFinalSubmit = () => {
    if (!validateCurrentStep()) {
      return;
    }

    // Audit submission
    interpreter.getAuditCollector().record(
      'form_submitted',
      {
        form_data: interpreter.getFormData(),
        primitives_generated: interpreter.getPrimitivesMap().size,
        steps_completed: totalSteps,
      },
      metadata.template_id,
      metadata.template_version,
      interpreter.getActor(),
      metadata.compliance.frameworks,
    );

    // Return generated primitives
    if (onSubmit) {
      onSubmit(interpreter.getPrimitives());
    }
  };

  // Generate schema for current step only
  const currentStepSchema = interpreter.generateJSONSchema(activeStep);
  const currentStepUISchema = interpreter.generateUISchema(activeStep);
  const currentStep = steps[activeStep];

  if (!currentStep) {
    return <Text>No step data available for step {activeStep}.</Text>;
  }

  return (
    <Stack gap="lg">
      {/* Validation Violations Display */}
      {validationResult && (
        <ViolationsDisplay
          violations={validationResult.violations}
          validationResult={validationResult}
        />
      )}

      {/* Mantine Stepper */}
      <Stepper
        active={activeStep}
        onStepClick={(stepIndex) => {
          // Allow clicking on previous steps or current step
          if (stepIndex <= highestStepReached) {
            setActiveStep(stepIndex);
          }
        }}
        size="sm"
        iconSize={32}
      >
        {steps.map((step, index) => (
          <Stepper.Step
            key={step.id}
            label={step.label}
            description={step.description}
            color={stepErrors[index] ? 'red' : undefined}
            icon={stepErrors[index] ? <IconAlertCircle size={18} /> : undefined}
            completedIcon={<IconCheck size={18} />}
            allowStepClick={index <= highestStepReached}
          />
        ))}
      </Stepper>

      {/* Current Step Form */}
      <Paper p="xl" withBorder>
        <Stack gap="md">
          <div>
            <Text size="xl" fw={600}>
              {currentStep.label}
            </Text>
            {currentStep.description && (
              <Text size="sm" c="dimmed" mt={4}>
                {currentStep.description}
              </Text>
            )}
          </div>

          {stepErrors[activeStep] && (
            <Alert color="red" title="Required fields missing" icon={<IconAlertCircle />}>
              Please fill in all required fields before proceeding.
            </Alert>
          )}

          <Form
            schema={currentStepSchema}
            uiSchema={currentStepUISchema}
            formData={interpreter.getFormData()}
            validator={validator}
            widgets={MANTINE_WIDGETS}
            templates={{ FieldTemplate: CustomFieldTemplate }}
            onChange={({ formData }) => {
              // Update form data
              interpreter.setFormData(formData || {});

              // Map each field to primitives
              Object.entries(interpreter.getFormData()).forEach(([fieldId, value]) => {
                interpreter.handleFieldChange(fieldId, value);
              });

              // Clear error for this step if user is filling fields
              if (stepErrors[activeStep]) {
                setStepErrors((prev) => ({ ...prev, [activeStep]: false }));
              }
            }}
            onError={(errors) => {
              // Audit validation errors
              interpreter.getAuditCollector().record(
                'validation_error',
                {
                  errors: errors,
                  field_count: Object.keys(errors).length,
                  step_index: activeStep,
                  step_id: currentStep.id,
                },
                metadata.template_id,
                metadata.template_version,
                interpreter.getActor(),
              );
            }}
          >
            {/* Hide default submit button - we use custom navigation */}
            <div style={{ display: 'none' }} />
          </Form>
        </Stack>
      </Paper>

      {/* Navigation Buttons */}
      <Group justify="space-between">
        <Button variant="subtle" onClick={() => console.log('Cancel')} disabled={false}>
          Cancel
        </Button>

        <Group>
          {activeStep > 0 && (
            <Button variant="default" onClick={handlePrevious}>
              Previous
            </Button>
          )}

          {!isLastStep && (
            <Button onClick={handleNext} rightSection={<span>→</span>}>
              Next
            </Button>
          )}

          {isLastStep && (
            <Button
              onClick={handleFinalSubmit}
              color="green"
              disabled={!canSubmit}
              title={hasErrorViolations ? 'Fix all errors before submitting' : undefined}
            >
              {metadata.ui.actions?.submit?.label || 'Complete Registration'}
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
}

/**
 * Primitive node instance
 */
interface PrimitiveNode {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
  metadata: Record<string, any>;
}

/**
 * Main Metadata Interpreter
 */
export class MetadataInterpreter {
  private metadata: WorkflowTemplateMetadata;
  private primitives: Map<string, PrimitiveNode>;
  private formData: Record<string, any>;
  private transformationEngine: TransformationEngine;
  private auditCollector: AuditTrailCollector;
  private actor: string;
  private validationResult?: ValidationResult;

  constructor(metadata: WorkflowTemplateMetadata, actor: string = 'user') {
    console.log('[MetadataInterpreter] Initializing with metadata:', metadata.template_id);

    // Initialize engines
    this.transformationEngine = new TransformationEngine();
    this.auditCollector = new AuditTrailCollector();
    this.actor = actor;

    // Validate metadata (life-critical: strict validation)
    this.validateMetadata(metadata);

    this.metadata = metadata;
    this.primitives = new Map();
    this.formData = {};

    // Initialize primitives from template
    this.initializePrimitives();

    // Audit initialization
    this.auditCollector.record(
      'metadata_loaded',
      {
        template_id: metadata.template_id,
        template_version: metadata.template_version,
        primitives_count: this.primitives.size,
        domain: metadata.business.domain,
      },
      metadata.template_id,
      metadata.template_version,
      this.actor,
      metadata.compliance.frameworks,
    );
  }

  /**
   * Validate metadata conforms to all FERP principles
   * Life-critical: Strict validation, fail fast
   */
  private validateMetadata(metadata: WorkflowTemplateMetadata): void {
    console.log('[MetadataInterpreter] Validating metadata...');

    // 1. Schema version
    if (metadata.schema_version !== '2.0') {
      throw new Error(
        `${VALIDATION_ERROR_CODES.INVALID_SCHEMA_VERSION}: ` +
          `Schema version must be 2.0, got ${metadata.schema_version}`,
      );
    }

    // 2. Required fields
    const requiredFields = [
      'template_id',
      'business',
      'ui',
      'primitives',
      'mappings',
      'compliance',
    ];
    requiredFields.forEach((field) => {
      if (!metadata[field as keyof WorkflowTemplateMetadata]) {
        throw new Error(
          `${VALIDATION_ERROR_CODES.MISSING_REQUIRED_FIELD}: ` +
            `Required field "${field}" is missing`,
        );
      }
    });

    // 3. Validate primitive types (CRITICAL!)
    this.validatePrimitiveTypes(metadata);

    // 4. Validate all UI fields have mappings
    this.validateAllFieldsMapped(metadata);

    // 5. Validate mappings reference existing primitives
    this.validateMappingReferences(metadata);

    console.log('[MetadataInterpreter] ✅ Validation passed');
  }

  /**
   * Validate all primitive types are in the 16 allowed types
   * CRITICAL: Core architectural constraint
   */
  private validatePrimitiveTypes(metadata: WorkflowTemplateMetadata): void {
    metadata.primitives.nodes.forEach((node, index) => {
      if (!isValidPrimitiveType(node.type)) {
        throw new Error(
          `${VALIDATION_ERROR_CODES.PRIMITIVE_NOT_IN_SIXTEEN}: ` +
            `Node ${index} has invalid type "${node.type}". ` +
            `Must be one of 16 primitive types: ${SIXTEEN_PRIMITIVE_TYPES.join(', ')}`,
        );
      }
    });

    console.log('[MetadataInterpreter] ✅ All primitive types valid (16 types only)');
  }

  /**
   * Validate all UI fields map to at least one primitive
   * Life-critical: No data loss, complete audit trail
   */
  private validateAllFieldsMapped(metadata: WorkflowTemplateMetadata): void {
    const allUIFields: string[] = [];

    // Extract all field IDs from UI schema
    metadata.ui.steps.forEach((step) => {
      step.fields.forEach((field) => {
        allUIFields.push(field.id);
      });
    });

    // Verify each has mapping
    allUIFields.forEach((fieldId) => {
      const mappings = metadata.mappings.ui_to_primitive[fieldId];

      if (!mappings || mappings.length === 0) {
        throw new Error(
          `${VALIDATION_ERROR_CODES.UNMAPPED_UI_FIELD}: ` +
            `UI field "${fieldId}" has no primitive mapping. ` +
            `All fields must map to at least one primitive for audit compliance.`,
        );
      }
    });

    console.log('[MetadataInterpreter] ✅ All UI fields mapped to primitives');
  }

  /**
   * Validate mappings reference primitives that exist in composition
   */
  private validateMappingReferences(metadata: WorkflowTemplateMetadata): void {
    Object.entries(metadata.mappings.ui_to_primitive).forEach(([fieldId, mappings]) => {
      mappings.forEach((mapping) => {
        const primitiveExists = metadata.primitives.nodes.some(
          (n) => n.id === mapping.primitive_id && n.type === mapping.primitive_type,
        );

        if (!primitiveExists) {
          throw new Error(
            `${VALIDATION_ERROR_CODES.PRIMITIVE_ID_NOT_FOUND}: ` +
              `Field "${fieldId}" maps to non-existent primitive "${mapping.primitive_id}" ` +
              `of type "${mapping.primitive_type}"`,
          );
        }
      });
    });

    console.log('[MetadataInterpreter] ✅ All mappings reference valid primitives');
  }

  /**
   * Initialize primitive instances from template
   * Preserves all metadata fields
   */
  private initializePrimitives(): void {
    this.metadata.primitives.nodes.forEach((nodeTemplate) => {
      const primitive: PrimitiveNode = {
        id: nodeTemplate.id,
        type: nodeTemplate.type,
        name: nodeTemplate.name,
        x: nodeTemplate.position?.x || 0,
        y: nodeTemplate.position?.y || 0,
        metadata: {
          // Preserve ALL template metadata
          ...nodeTemplate.metadata,

          // Add metadata tracking
          _generated_from_template: this.metadata.template_id,
          _template_version: this.metadata.template_version,
          _schema_version: this.metadata.schema_version,
          _ui_sources: [], // Track which UI fields populate this
          _transformations_applied: [], // Track transformations

          // Audit trail
          _audit: {
            created_at: new Date().toISOString(),
            created_by: 'metadata_interpreter',
            template_id: this.metadata.template_id,
            template_version: this.metadata.template_version,
          },

          // Business context
          _business_context: {
            business_name: this.metadata.business.name,
            business_category: this.metadata.business.category,
            business_domain: this.metadata.business.domain,
          },
        },
      };

      this.primitives.set(primitive.id, primitive);
    });

    console.log('[MetadataInterpreter] Initialized', this.primitives.size, 'primitives');
  }

  /**
   * Get generated primitives
   * Returns primitives ready for DynamoDB storage
   */
  getPrimitives(): {
    nodes: PrimitiveNode[];
    connections: Array<{ source: string; target: string; condition?: string; label?: string }>;
  } {
    return {
      nodes: Array.from(this.primitives.values()),
      connections: this.metadata.primitives.connections,
    };
  }

  /**
   * Get audit trail
   * Complete log of all interpreter operations
   */
  getAuditTrail(): AuditEntry[] {
    return this.auditCollector.getEntries();
  }

  /**
   * Export audit trail for compliance reporting
   */
  exportAuditTrail(): string {
    return this.auditCollector.export();
  }

  /**
   * Get metadata (public accessor for StepperFormWrapper)
   */
  getMetadata(): WorkflowTemplateMetadata {
    return this.metadata;
  }

  /**
   * Get form data (public accessor for StepperFormWrapper)
   */
  getFormData(): Record<string, any> {
    return this.formData;
  }

  /**
   * Set form data (public mutator for StepperFormWrapper)
   */
  setFormData(data: Record<string, any>): void {
    this.formData = data;
  }

  /**
   * Get audit collector (public accessor for StepperFormWrapper)
   */
  getAuditCollector(): AuditTrailCollector {
    return this.auditCollector;
  }

  /**
   * Get actor (public accessor for StepperFormWrapper)
   */
  getActor(): string {
    return this.actor;
  }

  /**
   * Get primitives map (public accessor for StepperFormWrapper)
   */
  getPrimitivesMap(): Map<string, PrimitiveNode> {
    return this.primitives;
  }

  /**
   * Set validation result from Phase 11 Validation Framework
   */
  setValidationResult(result: ValidationResult): void {
    this.validationResult = result;
  }

  /**
   * Get validation result
   */
  getValidationResult(): ValidationResult | undefined {
    return this.validationResult;
  }

  /**
   * Generate JSON Schema from UI metadata (public for StepperFormWrapper)
   * Converts FERP UI metadata to standard JSON Schema
   * @param stepIndex - Optional: Generate schema for specific step only
   */
  generateJSONSchema(stepIndex?: number): RJSFSchema {
    return this.generateJSONSchemaInternal(stepIndex);
  }

  /**
   * Generate UI Schema for React JSON Schema Form (public for StepperFormWrapper)
   * Maps FERP UI metadata to RJSF UI Schema
   * @param stepIndex - Optional: Generate UI schema for specific step only
   */
  generateUISchema(stepIndex?: number): UiSchema {
    return this.generateUISchemaInternal(stepIndex);
  }

  /**
   * Handle form field change
   * Single code path: Form change → Transformation → Primitive update → Audit
   */
  handleFieldChange(fieldId: string, value: any): void {
    console.log('[MetadataInterpreter] Field changed:', fieldId, value);

    // Get field mappings from metadata
    const mappings = this.metadata.mappings.ui_to_primitive[fieldId];

    if (!mappings || mappings.length === 0) {
      console.warn(`[MetadataInterpreter] No mappings found for field: ${fieldId}`);
      return;
    }

    // Update form data
    this.formData[fieldId] = value;

    // Map to each primitive
    mappings.forEach((mapping) => {
      this.updatePrimitiveField(fieldId, mapping, value);
    });
  }

  /**
   * Update primitive field with transformation
   * Life-critical: All updates logged with transformation trail
   */
  private updatePrimitiveField(fieldId: string, mapping: PrimitiveFieldMapping, value: any): void {
    const primitive = this.primitives.get(mapping.primitive_id);

    if (!primitive) {
      console.error(`[MetadataInterpreter] Primitive not found: ${mapping.primitive_id}`);
      return;
    }

    // Apply transformation
    const transformationResult = this.transformationEngine.transform(
      value,
      mapping.transformation,
      {
        value,
        formData: this.formData,
        primitives: this.primitives,
        metadata: this.metadata,
        timestamp: new Date().toISOString(),
        actor: this.actor,
      },
    );

    // Set field using JSONPath
    const result = jsonpath.JSONPath({
      path: mapping.field_path,
      json: primitive,
      callback: () => transformationResult.transformed_value,
      wrap: false,
    });

    // Track UI source in primitive metadata
    if (!primitive.metadata._ui_sources) {
      primitive.metadata._ui_sources = [];
    }

    primitive.metadata._ui_sources.push({
      ui_field_id: fieldId,
      ui_field_label: this.getFieldLabel(fieldId),
      set_at: new Date().toISOString(),
      original_value: value,
      transformed_value: transformationResult.transformed_value,
      transformation_type: mapping.transformation?.type || 'direct',
    });

    // Update primitive
    this.primitives.set(mapping.primitive_id, primitive);

    // Audit transformation
    this.auditCollector.record(
      'transformation_applied',
      {
        field_id: fieldId,
        primitive_id: mapping.primitive_id,
        primitive_type: mapping.primitive_type,
        field_path: mapping.field_path,
        ...transformationResult.audit,
      },
      this.metadata.template_id,
      this.metadata.template_version,
      this.actor,
      mapping.audit_tracking?.compliance_flags,
    );

    // Audit primitive update
    this.auditCollector.record(
      'primitive_updated',
      {
        primitive_id: mapping.primitive_id,
        primitive_type: mapping.primitive_type,
        field_path: mapping.field_path,
        old_value: result,
        new_value: transformationResult.transformed_value,
        source_ui_field: fieldId,
      },
      this.metadata.template_id,
      this.metadata.template_version,
      this.actor,
    );
  }

  /**
   * Get field label from metadata
   */
  private getFieldLabel(fieldId: string): string {
    for (const step of this.metadata.ui.steps) {
      const field = step.fields.find((f) => f.id === fieldId);
      if (field) {
        return field.label;
      }
    }
    return fieldId;
  }

  /**
   * Generate JSON Schema from UI metadata (internal implementation)
   * Converts FERP UI metadata to standard JSON Schema
   * @param stepIndex - Optional: Generate schema for specific step only
   */
  private generateJSONSchemaInternal(stepIndex?: number): RJSFSchema {
    const schema: RJSFSchema = {
      type: 'object',
      title: this.metadata.business.name,
      description: this.metadata.business.description,
      properties: {},
      required: [],
    };

    // Determine which steps to process
    const stepAtIndex = stepIndex !== undefined ? this.metadata.ui.steps[stepIndex] : undefined;
    const stepsToProcess = stepAtIndex ? [stepAtIndex] : (stepIndex !== undefined ? [] : this.metadata.ui.steps);

    // Extract fields from specified step(s)
    stepsToProcess.forEach((step) => {
      step.fields.forEach((field) => {
        // Map field to JSON Schema property
        // Use title here - React JSON Schema Form will display it
        const propertySchema: any = {
          title: field.label,
          description: field.description || field.helper_text,
        };

        // Infer type from component
        switch (field.component) {
          case 'TextInput':
          case 'Textarea':
          case 'PasswordInput':
            propertySchema.type = 'string';
            if (field.validation?.min_length)
              propertySchema.minLength = field.validation.min_length;
            if (field.validation?.max_length)
              propertySchema.maxLength = field.validation.max_length;
            if (field.validation?.pattern) propertySchema.pattern = field.validation.pattern;
            break;

          case 'NumberInput':
          case 'Slider':
          case 'Rating':
            propertySchema.type = 'number';
            if (field.validation?.min !== undefined) propertySchema.minimum = field.validation.min;
            if (field.validation?.max !== undefined) propertySchema.maximum = field.validation.max;
            break;

          case 'DateInput':
          case 'DateTimePicker':
          case 'TimeInput':
            propertySchema.type = 'string';
            propertySchema.format = 'date-time';
            break;

          case 'Checkbox':
          case 'Switch':
            propertySchema.type = 'boolean';
            break;

          case 'Select':
            propertySchema.type = 'string';
            if (field.data_source?.static_options) {
              propertySchema.enum = field.data_source.static_options.map((o) => o.value);
              propertySchema.enumNames = field.data_source.static_options.map((o) => o.label);
            }
            break;

          case 'MultiSelect':
            propertySchema.type = 'array';
            propertySchema.items = { type: 'string' };
            if (field.data_source?.static_options) {
              propertySchema.items.enum = field.data_source.static_options.map((o) => o.value);
            }
            break;

          case 'JsonEditor':
            propertySchema.type = 'object';
            break;

          default:
            propertySchema.type = 'string';
        }

        schema.properties![field.id] = propertySchema;

        if (field.required) {
          (schema.required as string[]).push(field.id);
        }
      });
    });

    return schema;
  }

  /**
   * Generate UI Schema for React JSON Schema Form (internal implementation)
   * Maps FERP UI metadata to RJSF UI Schema
   * @param stepIndex - Optional: Generate UI schema for specific step only
   */
  private generateUISchemaInternal(stepIndex?: number): UiSchema {
    const uiSchema: UiSchema = {};

    // Determine which steps to process
    const uiStepAtIndex = stepIndex !== undefined ? this.metadata.ui.steps[stepIndex] : undefined;
    const stepsToProcess = uiStepAtIndex ? [uiStepAtIndex] : (stepIndex !== undefined ? [] : this.metadata.ui.steps);

    stepsToProcess.forEach((step) => {
      step.fields.forEach((field) => {
        uiSchema[field.id] = {
          'ui:widget': field.component,
          'ui:placeholder': field.placeholder,
          'ui:help': field.helper_text,
          'ui:options': field.ui_props || {},
        };
      });
    });

    return uiSchema;
  }

  /**
   * Render UI using React JSON Schema Form
   * Single code path: Metadata → JSON Schema → UI → Primitives
   * Life-critical: All interactions audited
   */
  renderUI(
    onSubmit?: (primitives: { nodes: PrimitiveNode[]; connections: any[] }) => void,
  ): React.ReactElement {
    // Check if stepper layout
    if (this.metadata.ui.layout === 'stepper' && this.metadata.ui.steps.length > 1) {
      return (
        <StepperFormWrapper
          interpreter={this}
          onSubmit={onSubmit}
          validationResult={this.validationResult}
        />
      );
    }

    // Single-page layout (original behavior)
    const jsonSchema = this.generateJSONSchemaInternal();
    const uiSchema = this.generateUISchemaInternal();

    return (
      <Form
        schema={jsonSchema}
        uiSchema={uiSchema}
        formData={this.formData}
        validator={validator}
        widgets={MANTINE_WIDGETS}
        templates={{ FieldTemplate: CustomFieldTemplate }}
        onChange={({ formData }) => {
          // Update form data
          this.formData = formData || {};

          // Map each field to primitives
          Object.entries(this.formData).forEach(([fieldId, value]) => {
            this.handleFieldChange(fieldId, value);
          });
        }}
        onSubmit={({ formData }) => {
          // Audit submission
          this.auditCollector.record(
            'form_submitted',
            {
              form_data: formData,
              primitives_generated: this.primitives.size,
            },
            this.metadata.template_id,
            this.metadata.template_version,
            this.actor,
            this.metadata.compliance.frameworks,
          );

          // Return generated primitives
          if (onSubmit) {
            onSubmit(this.getPrimitives());
          }
        }}
        onError={(errors) => {
          // Audit validation errors
          this.auditCollector.record(
            'validation_error',
            {
              errors: errors,
              field_count: Object.keys(errors).length,
            },
            this.metadata.template_id,
            this.metadata.template_version,
            this.actor,
          );
        }}
      >
        <Group justify="space-between" mt="md">
          <Button type="button" variant="subtle" onClick={() => console.log('Cancel')}>
            Cancel
          </Button>
          <Button type="submit">{this.metadata.ui.actions?.submit?.label || 'Submit'}</Button>
        </Group>
      </Form>
    );
  }

  /**
   * Translate business event to user-friendly label
   * For timeline display
   */
  translateEvent(technicalEvent: string): {
    label: string;
    description: string;
    icon?: string;
    severity?: string;
  } {
    const translation = this.metadata.mappings.event_labels[technicalEvent];

    if (translation) {
      return {
        label: translation.business_label,
        description: translation.business_description,
        icon: translation.icon,
        severity: translation.severity,
      };
    }

    // Fallback: Use technical event name
    return {
      label: technicalEvent,
      description: `Technical event: ${technicalEvent}`,
    };
  }
}
